import { RA_VESSEL_GROUPS, type RaLibraryStatus } from '@felix/domain';
import { ActionIcon, Button, Select } from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../format.js';
import { StatusBadge } from '../../../StatusBadge.js';
import { useIssueText } from './RaBuilder.js';
import classes from './RaLibrary.module.css';
import type { RaLibraryDetail, RaVersionSummary } from './types.js';
import { assignmentLabel } from './view.js';

const FLOW: readonly RaLibraryStatus[] = [
  'draft',
  'submitted',
  'under_review',
  'approved',
  'published',
];

/** Durumun tonu (liste ve başlık). */
export function statusTone(status: RaLibraryStatus) {
  if (status === 'published') return 'success' as const;
  if (status === 'returned') return 'danger' as const;
  if (status === 'draft') return 'neutral' as const;
  return 'info' as const;
}

export function RaStatusBadge({ status }: { status: RaLibraryStatus }) {
  const { t } = useTranslation();
  return <StatusBadge tone={statusTone(status)}>{t(`riskLibrary.statuses.${status}`)}</StatusBadge>;
}

/** Yayımlı sürümlerin listesi (ofis ve gemi). */
export function VersionList({ versions }: { versions: readonly RaVersionSummary[] }) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskLibrary.publication.${key}`, options);
  if (versions.length === 0) return <p className={classes.muted}>{k('versionsNone')}</p>;
  return (
    <ul className={classes.history}>
      {versions.map((v) => (
        <li key={v.id} className={classes.historyItem}>
          <strong>
            {v.migrated
              ? k('migratedVersion', { revision: v.revision, at: formatDateTime(v.publishedAt) })
              : k('version', {
                  revision: v.revision,
                  at: formatDateTime(v.publishedAt),
                  by: v.publishedByName ?? '—',
                })}
          </strong>
          <span className={classes.muted}>
            {v.matrix ? k('matrix', { version: v.matrix.version }) : k('legacy')}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Sekme 7: iş akışı, yayım kapısının sorunları, yükseltme kabulü, geçmiş, yayımlı sürümler ve gemi
 * ataması (FD §5 Tab 7, §12; R13–R15).
 */
export function RaPublication({
  detail,
  vessels,
  busy,
  onAssign,
  onUnassign,
}: {
  detail: RaLibraryDetail;
  vessels: readonly { id: string; name: string }[];
  busy: boolean;
  onAssign: (target: { vesselGroup: string | null; vesselId: string | null }) => void;
  onUnassign: (assignmentId: string, rowVersion: number) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskLibrary.publication.${key}`, options);
  const issueText = useIssueText(detail.content);
  const [group, setGroup] = useState<string | null>(null);
  const [vessel, setVessel] = useState<string | null>(null);
  const groupName = (g: string) =>
    g === 'ALL'
      ? t('riskMaster.groups.ALL')
      : k('groupLabel', { group: t(`riskMaster.groups.${g}`) });
  const role = (r: string) => t(`risk.matrixProfile.acceptors.${r}`);
  const index = FLOW.indexOf(detail.status === 'returned' ? 'draft' : detail.status);
  const editing = detail.status === 'draft' || detail.status === 'returned';
  return (
    <div className={classes.columns}>
      <div className={classes.body} style={{ padding: 0 }}>
        <section className={classes.card} aria-label={k('workflow')}>
          <div className={classes.cardHead}>
            <span>{k('workflow')}</span>
            <RaStatusBadge status={detail.status} />
          </div>
          <div className={classes.cardBody}>
            <ol className={classes.steps}>
              {FLOW.map((s, i) => (
                <li
                  key={s}
                  className={classes.step}
                  data-active={s === detail.status || undefined}
                  data-done={i < index || undefined}
                >
                  {t(`riskLibrary.statuses.${s}`)}
                </li>
              ))}
              {detail.status === 'returned' ? (
                <li className={classes.step} data-active>
                  {t('riskLibrary.statuses.returned')}
                </li>
              ) : null}
            </ol>
            {editing ? (
              <>
                <strong>{k('issues')}</strong>
                {detail.issues.length === 0 ? (
                  <p className={classes.ok}>{k('issuesNone')}</p>
                ) : (
                  <ul className={classes.issues}>
                    {detail.issues.map((issue) => (
                      <li key={`${issue.code}-${issue.rowId ?? ''}-${issue.controlId ?? ''}`}>
                        {issueText(issue)}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : null}
          </div>
        </section>
        <section className={classes.card} aria-label={k('acceptors')}>
          <div className={classes.cardHead}>
            <span>{k('acceptors')}</span>
          </div>
          <div className={classes.cardBody}>
            {detail.acceptors.required.length === 0 ? (
              <p className={classes.muted}>{k('acceptorsNone')}</p>
            ) : (
              <div className={classes.chips}>
                {detail.acceptors.required.map((r) => (
                  <StatusBadge
                    key={r}
                    tone={detail.acceptors.accepted.includes(r) ? 'success' : 'warning'}
                  >
                    {role(r)} ·{' '}
                    {detail.acceptors.accepted.includes(r) ? k('accepted') : k('pending')}
                  </StatusBadge>
                ))}
              </div>
            )}
            {detail.acceptors.vessel.length > 0 ? (
              <p className={classes.muted}>
                {k('acceptorsVessel', { roles: detail.acceptors.vessel.map(role).join(', ') })}
              </p>
            ) : null}
          </div>
        </section>
        <section className={classes.card} aria-label={k('history')}>
          <div className={classes.cardHead}>
            <span>{k('history')}</span>
          </div>
          <div className={classes.cardBody}>
            {detail.events.length === 0 ? (
              <p className={classes.muted}>{k('historyNone')}</p>
            ) : (
              <ul className={classes.history}>
                {detail.events.map((e) => (
                  <li key={e.id} className={classes.historyItem}>
                    <strong>
                      {e.revision} ·{' '}
                      {t(`riskLibrary.events.${e.kind}`, {
                        role: e.acceptor ? role(e.acceptor) : '',
                      })}
                    </strong>
                    <span>{e.byName ?? '—'}</span>
                    <time className={classes.muted} dateTime={e.at}>
                      {formatDateTime(e.at)}
                    </time>
                    {e.sameActor ? <span className={classes.muted}>({k('sameActor')})</span> : null}
                    {e.reason ? <span className={classes.muted}>“{e.reason}”</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
      <div className={classes.body} style={{ padding: 0 }}>
        <section className={classes.card} aria-label={k('versions')}>
          <div className={classes.cardHead}>
            <span>{k('versions')}</span>
          </div>
          <div className={classes.cardBody}>
            <VersionList versions={detail.versions} />
          </div>
        </section>
        <section className={classes.card} aria-label={k('assignments')}>
          <div className={classes.cardHead}>
            <span>{k('assignments')}</span>
          </div>
          <div className={classes.cardBody}>
            <p className={classes.muted}>{k('assignmentsNote')}</p>
            {detail.assignments.length === 0 ? (
              <p className={classes.muted}>{k('assignmentsNone')}</p>
            ) : (
              <div className={classes.chips}>
                {detail.assignments.map((a) => {
                  const label = assignmentLabel(a, groupName);
                  return (
                    <span key={a.id} className={classes.chip}>
                      <StatusBadge tone="info">{label}</StatusBadge>
                      {detail.can.assign ? (
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          aria-label={k('unassign', { target: label })}
                          disabled={busy}
                          onClick={() => onUnassign(a.id, a.rowVersion)}
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      ) : null}
                    </span>
                  );
                })}
              </div>
            )}
            {detail.can.assign ? (
              <div className={classes.assign}>
                <Select
                  label={k('assignGroup')}
                  clearable
                  value={group}
                  data={RA_VESSEL_GROUPS.map((g) => ({ value: g, label: groupName(g) }))}
                  onChange={(v) => {
                    setGroup(v);
                    if (v) setVessel(null);
                  }}
                />
                <Select
                  label={k('assignVessel')}
                  clearable
                  searchable
                  value={vessel}
                  data={vessels.map((v) => ({ value: v.id, label: v.name }))}
                  onChange={(v) => {
                    setVessel(v);
                    if (v) setGroup(null);
                  }}
                />
                <Button
                  leftSection={<IconPlus size={16} />}
                  disabled={(!group && !vessel) || busy}
                  onClick={() => {
                    onAssign({ vesselGroup: group, vesselId: group ? null : vessel });
                    setGroup(null);
                    setVessel(null);
                  }}
                >
                  {k('assign')}
                </Button>
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}
